import React, { memo } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { tablet } from 'services/ui';
import { Colors } from 'context';

export interface ICareerLessonRow {
  order: number;
  title: string;
  summary: string;
  durationMin: number | null;
  isUnlocked: boolean;
  isCompleted: boolean;
  /** «Просмотрен» — opened at least once. Only meaningful while not completed. */
  isOpened: boolean;
  /** ★ marker when the student has bookmarked the lesson. */
  isBookmarked: boolean;
  onOpen: () => void;
}

/**
 * One row of the ordered career-lesson list.
 *
 * The lock is rendered from `isUnlocked` alone — the server already withheld
 * the lesson body for a locked row, so there is nothing to guess at here.
 * A locked row is not clickable and is not focusable, so keyboard users cannot
 * reach a dead control either.
 *
 * Three distinct states on the right marker (spec 0020 Фаза B/C): ✓ completed,
 * 🔒 locked, 👁 opened-but-not-completed («просмотрен»), → available-untouched.
 * A ★ is shown separately when the lesson is bookmarked.
 */
export const CareerLessonRow = memo(
  ({
    order,
    title,
    summary,
    durationMin,
    isUnlocked,
    isCompleted,
    isOpened,
    isBookmarked,
    onOpen,
  }: ICareerLessonRow) => {
    const { t } = useTranslation();

    const showOpened = isUnlocked && !isCompleted && isOpened;

    const stateLabel = isCompleted
      ? t('careerCenter.learning.state.completed')
      : !isUnlocked
      ? t('careerCenter.learning.state.locked')
      : showOpened
      ? t('careerCenter.learning.state.opened')
      : t('careerCenter.learning.state.open');

    const stateIcon = isCompleted ? '✓' : !isUnlocked ? '🔒' : showOpened ? '👁' : '→';

    return (
      <Row
        isUnlocked={isUnlocked}
        role={isUnlocked ? 'button' : undefined}
        tabIndex={isUnlocked ? 0 : undefined}
        aria-disabled={!isUnlocked}
        onClick={isUnlocked ? onOpen : undefined}
        onKeyDown={
          isUnlocked
            ? (event: React.KeyboardEvent<HTMLDivElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen();
                }
              }
            : undefined
        }>
        <OrderBadge isCompleted={isCompleted} isUnlocked={isUnlocked}>
          {isCompleted ? '✓' : order}
        </OrderBadge>

        <RowBody>
          <RowTitle>
            {title}
            {isBookmarked ? (
              <BookmarkStar aria-label={t('careerCenter.learning.bookmarked')}>★</BookmarkStar>
            ) : null}
          </RowTitle>
          <RowSummary>{summary}</RowSummary>
          <RowMeta>
            {durationMin ? (
              <MetaItem>{t('careerCenter.learning.duration', { minutes: durationMin })}</MetaItem>
            ) : null}
            <MetaItem>{stateLabel}</MetaItem>
          </RowMeta>
        </RowBody>

        <StateIcon aria-hidden>{stateIcon}</StateIcon>
      </Row>
    );
  },
);

const Row = styled.div<{ isUnlocked: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  padding: 20px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 12px;
  background-color: ${Colors.white};

  ${({ isUnlocked }) =>
    isUnlocked
      ? css`
          cursor: pointer;

          &:hover {
            border-color: ${Colors.main};
          }
        `
      : css`
          cursor: not-allowed;
          background-color: ${Colors.bg_gray};
          color: ${Colors.gray_dark};
        `}

  ${tablet} {
    padding: 24px;
  }
`;

const OrderBadge = styled.div<{ isCompleted: boolean; isUnlocked: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}

  ${({ isCompleted, isUnlocked }) =>
    isCompleted
      ? css`
          background-color: ${Colors.marker_green};
          color: ${Colors.white};
        `
      : isUnlocked
      ? css`
          background-color: ${Colors.main};
          color: ${Colors.btn_text};
        `
      : css`
          background-color: ${Colors.system_gray};
          /* Номер закрытого урока — обычный цвет текста, а не приглушённый:
             gray_dark на system_gray даёт 3.57:1 при норме 4.5. Состояние
             различается ЗАЛИВКОЙ (зелёная пройден / жёлтая открыт / серая
             закрыт), так что читаемость цифры ничего не путает. */
          color: ${Colors.black};
        `}
`;

const RowBody = styled.div`
  flex-grow: 1;
  min-width: 0;
`;

const RowTitle = styled.div`
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.bold })}
  margin-bottom: 4px;
`;

const BookmarkStar = styled.span`
  margin-left: 8px;
  color: ${Colors.main};
  font-size: 14px;
  line-height: 1;
`;

const RowSummary = styled.div`
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
`;

const MetaItem = styled.span`
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.regular })}
  color: ${Colors.gray_dark};
`;

const StateIcon = styled.div`
  flex-shrink: 0;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;
